import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { DemandSourceSchema, ForecastPeriodSchema } from './manufacturing.js';
import { pagedQuery, pagedResponse } from './pagination.js';
import { MessageSchema, QtyStringSchema, UnitPrecisionSchema } from './primitives.js';
import { WorkOrderSchema } from './work-orders.js';
const c = initContract();
/**
 * MFG-03 — PRODUCTION PLANNING. "What should we make, when, and will we have
 * the parts and the machine time?"
 *
 * §12: "None — this screen does not touch money or stock." Nothing on this
 * contract writes a movement or a posting. The two operations that DO write
 * something real — Create Work Order from a demand row and Convert to WO from
 * a planned entry — go through the work-orders service, which is why they
 * return a `WorkOrderResponse`: BR-MFG-03-20 requires that "creating from a
 * demand row and converting a planned entry must produce the same work order
 * through the same path".
 *
 * BOTH BUTTONS WERE LIES IN THE PROTOTYPE, and §9 says so outright: "Create
 * Work Order navigated and wrote nothing; Convert to WO wrote only the
 * Converted latch and created no work order either." Both create a real job
 * here, carrying the row's item, quantity and date.
 */
// ── Shapes ──────────────────────────────────────────────────────────────────
/**
 * `N-4` for every quantity on MFG-03 — "ALWAYS with the unit written next to
 * them" — and `N-14` for where it goes: on the ROW, never in a column header,
 * because "items are counted in pieces, kilograms, litres, metres and boxes"
 * and one MRP table's rows do not share a unit.
 *
 * IT IS THE ITEM'S **STOCK** UNIT, and on this screen that is not the usual
 * `BR-MD-03-04d` answer — it is the answer the ARITHMETIC forces. A net
 * requirement is `gross − on hand − incoming` and a reorder target is `on hand`
 * against `items.reorder_point`; `stock_levels.on_hand` and `reorder_point` are
 * stock-unit figures, so every column that is netted against them is one too.
 * There is no document line here whose own unit could disagree: `demand` reads
 * `sales_order_lines.qty` and `production_forecasts.qty`, and neither table has
 * a `unit_id` column to be written in something else.
 *
 * `unitSymbol` is `''` and `unitPrecision` `0` only for an item whose stock
 * unit has been deleted out from under it — the same fallback
 * `work_orders.service.ts` uses for its output unit, and the same reason: a
 * missing unit prints a bare number rather than inventing a symbol.
 */
const itemStockUnit = {
    unitSymbol: z.string(),
    unitPrecision: UnitPrecisionSchema,
};
export const DemandRowSchema = z
    .object({
    /** A stable key per row: `so:<id>`, `fc:<id>`, `rop:<itemId>`. */
    key: z.string(),
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    ...itemStockUnit,
    qty: z.string(),
    dueDate: z.string(),
    /** Field 8: colour-coded per row — this is how you tell the kinds apart. */
    source: DemandSourceSchema,
    /** Line 2 of field 1: `FG-100 · Sales order SO-3391`. */
    sourceRef: z.string().nullable(),
    /** Field 1: the red badge when the parts for it are short. */
    isShort: z.boolean(),
    /** BR-MFG-03-18: an item with no active recipe cannot be planned or converted. */
    hasActiveBom: z.boolean(),
})
    .strict();
/** §5.5 — about FINISHED GOODS, not components. "The easiest mistake to make on this screen." */
export const ReorderTargetSchema = z
    .object({
    itemId: z.uuid(),
    itemName: z.string(),
    ...itemStockUnit,
    onHand: z.string(),
    reorderPoint: z.string(),
    isBelow: z.boolean(),
})
    .strict();
export const NetRequirementSchema = z
    .object({
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    ...itemStockUnit,
    grossReq: z.string(),
    onHand: z.string(),
    incoming: z.string(),
    netReq: z.string(),
    leadDays: z.number().int(),
    /** Field 26: `earliest due − lead days`. Empty when net is zero. */
    orderBy: z.string().nullable(),
    /** BR-MFG-03-06: the date is in the past, so the row is already late. */
    isLate: z.boolean(),
})
    .strict();
/** §5.8 — hours, bucketed by WEEK, one row per workstation string. */
export const CapacityRowSchema = z
    .object({
    /** Field 30: the workstation's friendly name, resolved from the routing's code. */
    resource: z.string(),
    /** Field 32: load carries ONE decimal, available NONE. Both, deliberately. */
    loadHours: z.string(),
    availableHours: z.string(),
    /** Field 32: `load ÷ available × 100`, and it is ALLOWED to exceed 100. */
    percent: z.number().int(),
    /** Field 31: `over by 4.5h`, one decimal. Null when the resource is not over. */
    overBy: z.string().nullable(),
    /** Field 33: green at ≤85 %, amber above 85 up to 100, red above 100. */
    band: z.enum(['comfortable', 'tight', 'overloaded']),
})
    .strict();
export const PlannedOrderSchema = z
    .object({
    id: z.uuid(),
    itemId: z.uuid(),
    itemName: z.string(),
    ...itemStockUnit,
    qty: z.string(),
    startDate: z.string(),
    dueDate: z.string(),
    /** §5.10: the bar's WIDTH. Not printed on the row. */
    durationDays: z.number().int(),
    /** §5.10: what Group by Workstation groups on. Not printed on the row. */
    workstation: z.string().nullable(),
    source: DemandSourceSchema,
    status: z.enum(['planned', 'converted']),
    /** §5.10 [fixed]: once converted the Convert button is replaced by this link. */
    workOrderId: z.uuid().nullable(),
    workOrderCode: z.string().nullable(),
})
    .strict();
/**
 * ── WHAT THIS OBJECT IS, AND WHAT IT DELIBERATELY NO LONGER CARRIES ────────
 *
 * MFG-03 is four stacked blocks (§4) and this used to be all four of them in
 * one object. At PART 20.7's volumes that object was **10.5 MB**: 5,528 demand
 * rows, 5,528 planned entries, 621 reorder targets, and 17,392 net
 * requirements — sent **twice**, because BR-MFG-03-07 makes the requisition
 * band a filtered view of the net-requirement table and the filter ran in
 * JavaScript after both arrays had been built. `netRequirements` and
 * `suggestedRequisitions` were 3.69 MB each of the 10.5.
 *
 * So the four LISTS are four paged routes below, and this object is what is
 * left: the run's parameters and §5.8's capacity table, which is bounded by
 * the tenant's workstations rather than by its trading. That is the shape the
 * mutations answer with too — a write returns the SCREEN's state, and every
 * list beside it is re-read at whatever page the planner was on.
 *
 * BR-MFG-03-07 survives the split, and is stronger for it: the band is not a
 * second array here, it is `GET …/plan/net-requirements?shortOnly=true` — the
 * SAME ROUTE as the table above it, with one predicate. There is no second
 * calculation available to drift.
 */
const productionPlanShape = z
    .object({
    /**
     * Field 14. NULL means "Not yet run — click Recalculate", and §5.6's
     * net-requirements card is hidden until this is non-null.
     */
    lastCalculatedAt: z.string().nullable(),
    /** §5.4's three parameters, echoed so each shows its value on the card. */
    horizonWeeks: z.number().int(),
    warehouseId: z.uuid().nullable(),
    includeForecasts: z.boolean(),
    capacity: CapacityRowSchema.array(),
    /** Field 34: one red pill when ANY resource is over. */
    isOverloaded: z.boolean(),
})
    .strict();
export const RunPlanRequestSchema = z
    .object({
    /** §5.4's three, and only these three. "Do not build them." */
    horizonWeeks: z.union([z.literal(4), z.literal(8), z.literal(12)]).optional(),
    warehouseId: z.uuid().nullable().optional(),
    includeForecasts: z.boolean().optional(),
})
    .strict();
export const AddForecastRequestSchema = z
    .object({
    itemId: z.uuid(),
    qty: QtyStringSchema,
    /** Field 11: a MONTH, typed as YYYY-MM. Not a date picker. */
    period: ForecastPeriodSchema,
})
    .strict();
export const CreateFromDemandRequestSchema = z
    .object({
    itemId: z.uuid(),
    qty: QtyStringSchema,
    dueDate: z.iso.date(),
    warehouseId: z.uuid(),
})
    .strict();
export const RescheduleRequestSchema = z
    .object({
    /** BR-MFG-03-14: whole days only. There is no finer drag. */
    startDate: z.iso.date().optional(),
    /** §8.1: what-if changes exactly two things, and this is the other one. */
    qty: QtyStringSchema.optional(),
})
    .strict();
/** §8.1's grey panel: `before → afterh / availableh`, with a triangle when over. */
export const WhatIfRowSchema = z
    .object({
    resource: z.string(),
    beforeHours: z.string(),
    afterHours: z.string(),
    availableHours: z.string(),
    overAfter: z.boolean(),
})
    .strict();
/**
 * ── Why this one schema is declared and not inferred ────────────────────────
 * `ProductionPlanSchema` answers 5 of this module's routes. Annotating it as
 * `z.ZodType<ProductionPlanResponse>` writes the shape ONCE and lets every route
 * reference it by name, which keeps this module's emitted declaration a few
 * kilobytes instead of a hundred. Runtime validation is unchanged: the object
 * schema above is still what parses.
 */
export const ProductionPlanSchema = productionPlanShape;
const errors = {
    400: MessageSchema,
    401: MessageSchema,
    403: MessageSchema,
    404: MessageSchema,
    409: MessageSchema,
};
export const productionPlanningContract = c.router({
    plan: {
        method: 'GET',
        path: '/api/manufacturing/plan',
        responses: {
            200: ProductionPlanSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        // The run's parameters and §5.8's capacity table. The four LISTS are the
        // four paged routes below (NFR-42); this is what is left when they go.
        summary: 'The screen’s header and capacity (manufacturing.view).',
    },
    /**
     * §5.1/§5.2 — EVERY source, always (BR-MFG-03-01a). Paging is not a view
     * that "drops a source": all three kinds are in one ordered list and the
     * pager states the total, so the rule's "no view may silently drop one"
     * holds — nothing is filtered out, and the count says how many there are.
     */
    demand: {
        method: 'GET',
        path: '/api/manufacturing/plan/demand',
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('rows', DemandRowSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: '§5.2 demand, PAGED (NFR-42). Every source, in one list.',
    },
    /**
     * §5.6's table AND §5.7's band, from ONE route.
     *
     * BR-MFG-03-07: "Exactly the components with a net requirement above zero
     * must appear as suggested purchase requisitions. The band must be a
     * filtered view of the net-requirements table, never a separate
     * calculation." `shortOnly=true` IS that filtered view — the same query,
     * the same ordering, one extra predicate — so the band cannot become a
     * second calculation, because there is no second query to become one.
     */
    netRequirements: {
        method: 'GET',
        path: '/api/manufacturing/plan/net-requirements',
        query: pagedQuery({
            /** BR-MFG-03-07's band: `net_req > 0`, as SQL, on the same read. */
            shortOnly: z.enum(['true']).optional(),
        }),
        responses: {
            200: pagedResponse('rows', NetRequirementSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: '§5.6 net requirements, PAGED; `shortOnly` is §5.7’s band.',
    },
    reorderTargets: {
        method: 'GET',
        path: '/api/manufacturing/plan/reorder-targets',
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('rows', ReorderTargetSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        // §5.5 — FINISHED GOODS below their reorder point, not components.
        summary: '§5.5 reorder targets, PAGED (NFR-42).',
    },
    plannedOrders: {
        method: 'GET',
        path: '/api/manufacturing/plan/planned-orders',
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('rows', PlannedOrderSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: '§5.10 the production schedule, PAGED (NFR-42).',
    },
    run: {
        method: 'POST',
        path: '/api/manufacturing/plan/run',
        body: RunPlanRequestSchema,
        responses: { 200: ProductionPlanSchema, ...errors },
        // Explodes every active recipe through every level of sub-assembly,
        // nets stock and OPEN supply, backward-schedules the order-by dates,
        // rebuilds capacity. BR-MFG-03-09: replaces every Planned entry and
        // leaves every Converted one alone.
        summary: 'Recalculate Plan (manufacturing.edit).',
    },
    addForecast: {
        method: 'POST',
        path: '/api/manufacturing/plan/forecasts',
        body: AddForecastRequestSchema,
        responses: { 201: ProductionPlanSchema, ...errors },
        // Finished goods only (BR-MFG-03-02). It does NOT re-run the plan —
        // §5.3 field 12 is explicit about that.
        summary: 'Add a monthly forecast (manufacturing.create).',
    },
    removeForecast: {
        method: 'DELETE',
        path: '/api/manufacturing/plan/forecasts/:forecastId',
        pathParams: z.object({ forecastId: z.uuid() }).strict(),
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Remove a forecast. It is a note, not a posted record.',
    },
    createFromDemand: {
        method: 'POST',
        path: '/api/manufacturing/plan/work-orders',
        body: CreateFromDemandRequestSchema,
        responses: { 201: WorkOrderSchema, ...errors },
        // BR-MFG-03-20. Creates ONE job carrying the row's item, quantity and
        // due date, and returns it: "a button that only navigates must not be
        // built". Same path as `convert` (BR-MFG-03-10).
        summary: 'The demand row Create Work Order.',
    },
    convert: {
        method: 'POST',
        path: '/api/manufacturing/plan/planned-orders/:plannedOrderId/convert',
        pathParams: z.object({ plannedOrderId: z.uuid() }).strict(),
        body: z.object({ warehouseId: z.uuid() }).strict(),
        responses: { 201: WorkOrderSchema, ...errors },
        // BR-MFG-03-10: exactly one work order, copying item, quantity and
        // start date, latching the entry to Converted. Never converted twice.
        summary: 'Convert a planned entry to a work order.',
    },
    reschedule: {
        method: 'PATCH',
        path: '/api/manufacturing/plan/planned-orders/:plannedOrderId',
        pathParams: z.object({ plannedOrderId: z.uuid() }).strict(),
        body: RescheduleRequestSchema,
        responses: { 200: ProductionPlanSchema, ...errors },
        // Dragging a bar (BR-MFG-03-14, whole days) and applying a what-if
        // change (BR-MFG-03-16, quantity and start date) are the same write, and
        // both recalculate capacity. A converted entry whose job has already
        // started is refused (BR-MFG-03-15).
        summary: 'Reschedule a planned entry.',
    },
    whatIf: {
        method: 'GET',
        path: '/api/manufacturing/plan/planned-orders/:plannedOrderId/what-if',
        pathParams: z.object({ plannedOrderId: z.uuid() }).strict(),
        query: z
            .object({ startDate: z.iso.date().optional(), qty: QtyStringSchema.optional() })
            .strict(),
        responses: {
            200: z.object({ rows: WhatIfRowSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        // The PREVIEW half only: reads, writes nothing, needs only the read
        // permission (BR-MFG-03-21). It previews CAPACITY and nothing else — not
        // material, not net requirements, not whether the due date can still be
        // met — and §8.1 asks the interface to say so.
        summary: '§8.1 What-if capacity preview.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=production-planning.js.map